// @ts-nocheck
import React, { useState, useEffect } from "react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { FileUploader } from "@/components/profile/FileUploader";
import { supabase } from "@/lib/supabase";
import { useAuth } from "@/contexts/AuthContext";
import { toast } from "sonner";
import { Trash2, Save, X, Plus, Loader2 } from "lucide-react";
import TeamRosterSection from "./TeamRosterSection";
import TeamSharingControls from "@/components/club/TeamSharingControls";
import { useSportsByGender } from "@/hooks/useSportsByGender";

interface Team {
  id: string;
  name: string;
  sport_id?: string;
  gender?: string;
  current_season?: string;
  roster_count: number;
  created_at?: string;
  age_group?: string;
  logo_url?: string;
  is_public?: boolean;
  stable_slug?: string;
}

interface StaffMember {
  id?: string;
  display_name: string;
  email: string;
  phone: string;
}

interface InlineTeamManagerProps {
  team?: Team;
  onTeamUpdated: () => void;
  isCreating?: boolean;
  onCancelCreate?: () => void;
}

const EMPTY_STAFF: StaffMember = { display_name: '', email: '', phone: '' };

const InlineTeamManager: React.FC<InlineTeamManagerProps> = ({
  team,
  onTeamUpdated,
  isCreating = false,
  onCancelCreate
}) => {
  const { profile } = useAuth();
  const [saving, setSaving] = useState(false);
  const [logoUploading, setLogoUploading] = useState(false);
  const [deleting, setDeleting] = useState(false);
  const [membersLoading, setMembersLoading] = useState(!isCreating && !!team?.id);

  // Form state
  const [formData, setFormData] = useState({
    name: team?.name || '',
    gender: team?.gender || '',
    sport_id: team?.sport_id || '',
    current_season: team?.current_season || '',
    age_group: team?.age_group || '',
    logo_url: team?.logo_url || '',
  });

  // Staff state
  const [headCoach, setHeadCoach] = useState<StaffMember>({ ...EMPTY_STAFF });
  const [assistants, setAssistants] = useState<StaffMember[]>([]);
  const [managers, setManagers] = useState<StaffMember[]>([]);

  // Track initial member IDs for diffing on save
  const [initialAssistantIds, setInitialAssistantIds] = useState<Set<string>>(new Set());
  const [initialManagerIds, setInitialManagerIds] = useState<Set<string>>(new Set());
  const [headCoachMemberId, setHeadCoachMemberId] = useState<string | undefined>();

  // Sports list (no gender filter — canonical sports table has null gender)
  const { sports, isLoading: sportsLoading } = useSportsByGender();

  // Self-fetch team members when editing an existing team
  useEffect(() => {
    if (!isCreating && team?.id) {
      fetchTeamMembers();
    }
  }, [team?.id, isCreating]);

  const fetchTeamMembers = async () => {
    if (!team?.id) return;
    setMembersLoading(true);
    try {
      const { data, error } = await supabase.rpc('get_team_with_members', { p_team_id: team.id });
      if (error) {
        console.error('Error fetching team members:', error);
        toast.error('Failed to load team staff');
        return;
      }
      if (!data || data.length === 0) return;

      // Refresh form data from canonical source
      const row = data[0];
      setFormData({
        name: row.team_display_name || '',
        gender: row.team_gender || '',
        sport_id: row.team_sport_id || '',
        current_season: row.team_current_season || '',
        age_group: row.team_age_group || '',
        logo_url: row.team_logo_url || '',
      });

      // Parse members by role (skip rows with null member_id — team with no members)
      const members = data.filter((r: any) => r.member_id != null);

      const hc = members.find((r: any) => r.member_role === 'head_coach');
      if (hc) {
        setHeadCoach({
          id: hc.member_id,
          display_name: hc.member_display_name || '',
          email: hc.member_email || '',
          phone: hc.member_phone || '',
        });
        setHeadCoachMemberId(hc.member_id);
      }

      const acs = members
        .filter((r: any) => r.member_role === 'assistant_coach')
        .map((r: any) => ({
          id: r.member_id as string,
          display_name: (r.member_display_name || '') as string,
          email: (r.member_email || '') as string,
          phone: (r.member_phone || '') as string,
        }));
      setAssistants(acs);
      setInitialAssistantIds(new Set(acs.map((a: StaffMember) => a.id!)));

      const mgrs = members
        .filter((r: any) => r.member_role === 'team_manager')
        .map((r: any) => ({
          id: r.member_id as string,
          display_name: (r.member_display_name || '') as string,
          email: (r.member_email || '') as string,
          phone: (r.member_phone || '') as string,
        }));
      setManagers(mgrs);
      setInitialManagerIds(new Set(mgrs.map((m: StaffMember) => m.id!)));
    } catch (error) {
      console.error('Error fetching team members:', error);
    } finally {
      setMembersLoading(false);
    }
  };

  const handleInputChange = (field: string, value: string) => {
    setFormData(prev => ({ ...prev, [field]: value }));
  };

  const handleGenderChange = (value: string) => {
    setFormData(prev => ({ ...prev, gender: value }));
  };

  // Staff list helpers
  const updateStaffList = (
    setList: React.Dispatch<React.SetStateAction<StaffMember[]>>,
    index: number,
    field: keyof StaffMember,
    value: string
  ) => {
    setList(prev => prev.map((m, i) => i === index ? { ...m, [field]: value } : m));
  };

  const addAssistant = () => setAssistants(prev => [...prev, { ...EMPTY_STAFF }]);
  const removeAssistant = (index: number) => setAssistants(prev => prev.filter((_, i) => i !== index));
  const addManager = () => setManagers(prev => [...prev, { ...EMPTY_STAFF }]);
  const removeManager = (index: number) => setManagers(prev => prev.filter((_, i) => i !== index));

  const handleDeleteTeam = async () => {
    if (!team?.id || !confirm('Are you sure you want to delete this team? This action cannot be undone.')) {
      return;
    }

    try {
      setDeleting(true);

      const { error } = await supabase
        .from('teams')
        .delete()
        .eq('id', team.id);

      if (error) {
        if (error.code === '23503') {
          toast.error("This team has event registrations and can't be deleted. Mark it inactive instead.");
        } else {
          console.error('Error deleting team:', error);
          toast.error('Failed to delete team');
        }
        return;
      }

      toast.success('Team deleted successfully');
      onTeamUpdated();
    } catch (error) {
      console.error('Error deleting team:', error);
      toast.error('Failed to delete team');
    } finally {
      setDeleting(false);
    }
  };

  const handleLogoUpload = async (file: File) => {
    if (!profile?.id) return;

    setLogoUploading(true);
    try {
      const fileExt = file.name.split('.').pop();
      const fileName = `${Date.now()}-${Math.random().toString(36).substring(2)}.${fileExt}`;

      // Delete existing logo if updating
      if (formData.logo_url && formData.logo_url.includes('team_logos')) {
        const existingPath = formData.logo_url.split('/team_logos/')[1];
        if (existingPath) {
          await supabase.storage
            .from('team_logos')
            .remove([existingPath]);
        }
      }

      const { data, error } = await supabase.storage
        .from('team_logos')
        .upload(fileName, file, {
          cacheControl: '3600',
          upsert: true
        });

      if (error) throw error;

      const { data: urlData } = supabase.storage
        .from('team_logos')
        .getPublicUrl(data.path);

      setFormData(prev => ({ ...prev, logo_url: urlData.publicUrl }));
      toast.success('Logo uploaded successfully');
    } catch (error) {
      console.error('Error uploading logo:', error);
      toast.error('Failed to upload logo');
    } finally {
      setLogoUploading(false);
    }
  };

  const handleDeleteLogo = async () => {
    if (!formData.logo_url) return;

    try {
      if (formData.logo_url.includes('team_logos')) {
        const path = formData.logo_url.split('/team_logos/')[1];
        if (path) {
          await supabase.storage
            .from('team_logos')
            .remove([path]);
        }
      }

      setFormData(prev => ({ ...prev, logo_url: '' }));
      toast.success('Logo removed');
    } catch (error) {
      console.error('Error removing logo:', error);
      toast.error('Failed to remove logo');
    }
  };

  const syncStaffMembers = async (
    teamId: string,
    role: string,
    current: StaffMember[],
    initialIds: Set<string>
  ) => {
    const currentIds = new Set(current.filter(m => m.id).map(m => m.id!));

    // Delete removed members
    for (const id of initialIds) {
      if (!currentIds.has(id)) {
        await supabase.from('team_members').delete().eq('id', id);
      }
    }

    // Update existing + insert new
    for (const member of current) {
      if (!member.display_name && !member.email) continue;

      if (member.id) {
        await supabase.from('team_members').update({
          display_name: member.display_name || null,
          email: member.email || null,
          phone: member.phone || null,
        }).eq('id', member.id);
      } else {
        await supabase.from('team_members').insert({
          team_id: teamId,
          role,
          display_name: member.display_name || null,
          email: member.email || null,
          phone: member.phone || null,
          status: 'active',
        });
      }
    }
  };

  const handleSave = async () => {
    if (!profile?.id || !formData.name.trim()) {
      toast.error('Team name is required');
      return;
    }

    setSaving(true);
    try {
      if (isCreating) {
        // Create team + head coach via RPC
        const { data: newTeamId, error } = await supabase.rpc('create_team_with_coach', {
          p_display_name: formData.name.trim(),
          p_sport_id: formData.sport_id || null,
          p_age_group: formData.age_group || null,
          p_gender: formData.gender || null,
          p_current_season: formData.current_season || null,
          p_logo_url: formData.logo_url || null,
          p_coach_user_id: profile.id,
          p_coach_display_name: headCoach.display_name || null,
          p_coach_email: headCoach.email || null,
          p_coach_phone: headCoach.phone || null,
          p_coach_role: 'head_coach',
        });

        if (error) throw error;

        // Insert assistants
        for (const asst of assistants) {
          if (asst.display_name || asst.email) {
            await supabase.from('team_members').insert({
              team_id: newTeamId,
              role: 'assistant_coach',
              display_name: asst.display_name || null,
              email: asst.email || null,
              phone: asst.phone || null,
              status: 'active',
            });
          }
        }

        // Insert managers
        for (const mgr of managers) {
          if (mgr.display_name || mgr.email) {
            await supabase.from('team_members').insert({
              team_id: newTeamId,
              role: 'team_manager',
              display_name: mgr.display_name || null,
              email: mgr.email || null,
              phone: mgr.phone || null,
              status: 'active',
            });
          }
        }

        toast.success('Team created successfully');
      } else {
        // Update team fields
        const { error: teamError } = await supabase
          .from('teams')
          .update({
            display_name: formData.name.trim(),
            sport_id: formData.sport_id || null,
            age_group: formData.age_group || null,
            gender: formData.gender || null,
            current_season: formData.current_season || null,
            logo_url: formData.logo_url || null,
          })
          .eq('id', team!.id);

        if (teamError) throw teamError;

        // Update head coach member
        if (headCoachMemberId) {
          await supabase.from('team_members').update({
            display_name: headCoach.display_name || null,
            email: headCoach.email || null,
            phone: headCoach.phone || null,
          }).eq('id', headCoachMemberId);
        }

        // Sync assistants and managers
        await syncStaffMembers(team!.id, 'assistant_coach', assistants, initialAssistantIds);
        await syncStaffMembers(team!.id, 'team_manager', managers, initialManagerIds);

        toast.success('Team updated successfully');
      }

      onTeamUpdated();
      if (isCreating && onCancelCreate) {
        onCancelCreate();
      }
    } catch (error) {
      console.error('Error saving team:', error);
      toast.error('Failed to save team');
    } finally {
      setSaving(false);
    }
  };

  if (membersLoading) {
    return (
      <div className="flex items-center justify-center py-8 gap-2 text-muted-foreground">
        <Loader2 className="h-4 w-4 animate-spin" />
        Loading team details...
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Team Management Form */}
      <div className="p-4 border rounded-lg bg-gray-50">
        {isCreating && (
          <div className="flex items-center justify-between mb-6">
            <h3 className="text-lg font-semibold">Create New Roster</h3>
            <Button
              variant="outline"
              size="sm"
              onClick={onCancelCreate}
            >
              <X className="h-4 w-4 mr-2" />
              Cancel
            </Button>
          </div>
        )}

        {/* Team Logo */}
        <div className="space-y-2 mb-6">
          <Label>Team Logo</Label>
          <div className="flex items-center gap-4">
            {formData.logo_url && (
              <div className="relative">
                <img
                  src={formData.logo_url}
                  alt="Team logo"
                  className="w-16 h-16 object-cover rounded-lg border"
                />
                <Button
                  type="button"
                  variant="outline"
                  size="sm"
                  className="absolute -top-2 -right-2 h-6 w-6 p-0"
                  onClick={handleDeleteLogo}
                >
                  <Trash2 className="h-3 w-3" />
                </Button>
              </div>
            )}
            <FileUploader
              onFileSelect={handleLogoUpload}
              acceptedFileTypes="image/*"
              maxSize={5}
              buttonText={logoUploading ? "Uploading..." : "Upload Logo"}
              disabled={logoUploading}
            />
          </div>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          {/* Basic Team Info */}
          <div className="space-y-4">
            <div>
              <Label htmlFor="team-name">Team Name *</Label>
              <Input
                id="team-name"
                value={formData.name}
                onChange={(e) => handleInputChange('name', e.target.value)}
                placeholder="Enter team name"
              />
            </div>

            <div>
              <Label htmlFor="gender">Gender *</Label>
              <Select
                value={formData.gender}
                onValueChange={handleGenderChange}
              >
                <SelectTrigger>
                  <SelectValue placeholder="Select gender" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="boys">Boys</SelectItem>
                  <SelectItem value="girls">Girls</SelectItem>
                  <SelectItem value="men">Men</SelectItem>
                  <SelectItem value="women">Women</SelectItem>
                  <SelectItem value="coed">Coed</SelectItem>
                </SelectContent>
              </Select>
            </div>

            <div>
              <Label htmlFor="sport">Sport</Label>
              <Select
                value={formData.sport_id}
                onValueChange={(value) => handleInputChange('sport_id', value)}
              >
                <SelectTrigger>
                  <SelectValue placeholder="Select sport" />
                </SelectTrigger>
                <SelectContent>
                  {sports.map((sport) => (
                    <SelectItem key={sport.id} value={sport.id}>
                      {sport.name}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>

            <div>
              <Label htmlFor="season">Season</Label>
              <Input
                id="season"
                value={formData.current_season}
                onChange={(e) => handleInputChange('current_season', e.target.value)}
                placeholder="e.g., Fall 2024"
              />
            </div>

            <div>
              <Label htmlFor="age-group">Age Group</Label>
              <Input
                id="age-group"
                value={formData.age_group}
                onChange={(e) => handleInputChange('age_group', e.target.value)}
                placeholder="e.g., 15U, 2026, etc."
              />
            </div>
          </div>

          {/* Staff Information */}
          <div className="space-y-6">
            {/* Head Coach */}
            <div className="space-y-3">
              <h4 className="font-medium text-sm text-gray-700">Head Coach</h4>
              <div className="space-y-2">
                <Input
                  value={headCoach.display_name}
                  onChange={(e) => setHeadCoach(prev => ({ ...prev, display_name: e.target.value }))}
                  placeholder="Head coach name"
                />
                <Input
                  type="email"
                  value={headCoach.email}
                  onChange={(e) => setHeadCoach(prev => ({ ...prev, email: e.target.value }))}
                  placeholder="Head coach email"
                />
                <Input
                  formatPhone
                  value={headCoach.phone}
                  onChange={(e) => setHeadCoach(prev => ({ ...prev, phone: e.target.value }))}
                  placeholder="Head coach phone"
                />
              </div>
            </div>

            {/* Assistants (list-based) */}
            <div className="space-y-3">
              <div className="flex items-center justify-between">
                <h4 className="font-medium text-sm text-gray-700">Assistants</h4>
                <Button type="button" variant="ghost" size="sm" onClick={addAssistant}>
                  <Plus className="h-3 w-3 mr-1" />
                  Add
                </Button>
              </div>
              {assistants.length === 0 && (
                <p className="text-xs text-muted-foreground">No assistants added.</p>
              )}
              {assistants.map((asst, idx) => (
                <div key={asst.id || `new-asst-${idx}`} className="space-y-2 p-3 border rounded-md relative">
                  <Button
                    type="button"
                    variant="ghost"
                    size="sm"
                    className="absolute top-1 right-1 h-6 w-6 p-0 text-muted-foreground hover:text-destructive"
                    onClick={() => removeAssistant(idx)}
                  >
                    <X className="h-3 w-3" />
                  </Button>
                  <Input
                    value={asst.display_name}
                    onChange={(e) => updateStaffList(setAssistants, idx, 'display_name', e.target.value)}
                    placeholder="Name"
                  />
                  <Input
                    type="email"
                    value={asst.email}
                    onChange={(e) => updateStaffList(setAssistants, idx, 'email', e.target.value)}
                    placeholder="Email"
                  />
                  <Input
                    formatPhone
                    value={asst.phone}
                    onChange={(e) => updateStaffList(setAssistants, idx, 'phone', e.target.value)}
                    placeholder="Phone"
                  />
                </div>
              ))}
            </div>

            {/* Team Managers (list-based) */}
            <div className="space-y-3">
              <div className="flex items-center justify-between">
                <h4 className="font-medium text-sm text-gray-700">Team Managers</h4>
                <Button type="button" variant="ghost" size="sm" onClick={addManager}>
                  <Plus className="h-3 w-3 mr-1" />
                  Add
                </Button>
              </div>
              {managers.length === 0 && (
                <p className="text-xs text-muted-foreground">No managers added.</p>
              )}
              {managers.map((mgr, idx) => (
                <div key={mgr.id || `new-mgr-${idx}`} className="space-y-2 p-3 border rounded-md relative">
                  <Button
                    type="button"
                    variant="ghost"
                    size="sm"
                    className="absolute top-1 right-1 h-6 w-6 p-0 text-muted-foreground hover:text-destructive"
                    onClick={() => removeManager(idx)}
                  >
                    <X className="h-3 w-3" />
                  </Button>
                  <Input
                    value={mgr.display_name}
                    onChange={(e) => updateStaffList(setManagers, idx, 'display_name', e.target.value)}
                    placeholder="Name"
                  />
                  <Input
                    type="email"
                    value={mgr.email}
                    onChange={(e) => updateStaffList(setManagers, idx, 'email', e.target.value)}
                    placeholder="Email"
                  />
                  <Input
                    formatPhone
                    value={mgr.phone}
                    onChange={(e) => updateStaffList(setManagers, idx, 'phone', e.target.value)}
                    placeholder="Phone"
                  />
                </div>
              ))}
            </div>
          </div>
        </div>

        {/* Save Button */}
        <div className="flex justify-end pt-4">
          <Button
            onClick={handleSave}
            disabled={saving}
            className="flex items-center gap-2"
          >
            <Save className="h-4 w-4" />
            {saving ? 'Saving...' : (isCreating ? 'Create Team' : 'Save Changes')}
          </Button>
        </div>
      </div>

      {/* Team Sharing Controls - Only show for existing teams */}
      {!isCreating && team && team.stable_slug && (
        <TeamSharingControls
          teamId={team.id}
          teamName={team.name}
          isPublic={team.is_public || false}
          slug={team.stable_slug}
          viewCount={0}
          onUpdate={onTeamUpdated}
        />
      )}

      {/* Team Roster Section - Only show for existing teams */}
      {!isCreating && team && (
        <TeamRosterSection
          teamId={team.id}
          onRosterUpdated={onTeamUpdated}
        />
      )}

      {/* Danger Zone - Delete Team (only for existing teams) */}
      {!isCreating && team && (
        <div className="p-4 border border-destructive/30 rounded-lg bg-destructive/5">
          <h4 className="text-base font-semibold text-destructive mb-2">Danger Zone</h4>
          <p className="text-sm text-muted-foreground mb-4">
            Permanently delete this team and all associated roster data. This action cannot be undone.
          </p>
          <Button
            onClick={handleDeleteTeam}
            disabled={deleting}
            variant="destructive"
            className="flex items-center gap-2"
          >
            <Trash2 className="h-4 w-4" />
            {deleting ? 'Deleting...' : 'Delete Team'}
          </Button>
        </div>
      )}
    </div>
  );
};

export default InlineTeamManager;